import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Search, SlidersHorizontal, X,
  ChevronLeft, ChevronRight, ArrowRightLeft,
  Landmark, CheckCircle2, AlertCircle, Clock, Loader2,
} from 'lucide-react';
import { withdrawalsAPI } from '../../api/withdrawals';
import PageHeader  from '../../components/ui/PageHeader';
import StatusBadge from '../../components/ui/StatusBadge';

// ── Constants ────────────────────────────────────────────────
const TRANSFER_METHODS = [
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'neft',          label: 'NEFT' },
  { value: 'rtgs',          label: 'RTGS' },
  { value: 'upi',           label: 'UPI' },
  { value: 'cheque',        label: 'Cheque' },
  { value: 'cash',          label: 'Cash' },
];

const TRANSFER_STATUSES = [
  { value: 'all',         label: 'All' },
  { value: 'pending',     label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed',   label: 'Completed' },
  { value: 'failed',      label: 'Failed' },
];

const PER_PAGE = 15;

// ── Helpers ──────────────────────────────────────────────────
const formatINR = (val) =>
  `₹${Number(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

const formatDate = (val) => {
  if (!val) return '—';
  return new Date(val).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

const formatPeriod = (val) => {
  if (!val) return '—';
  return val.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * Calculates deadline = requestedAt + months from withdrawal_period.
 * Returns { deadline, remainingDays, isOverdue, isDueToday, isDueSoon }.
 */
const getDeadlineInfo = (requestedAt, withdrawalPeriod) => {
  if (!requestedAt || !withdrawalPeriod) return null;
  const months = parseInt(withdrawalPeriod.split('_')[0], 10);
  if (isNaN(months)) return null;

  const deadline = new Date(requestedAt);
  deadline.setMonth(deadline.getMonth() + months);
  deadline.setHours(23, 59, 59, 999);

  const today       = new Date();
  today.setHours(0, 0, 0, 0);
  const diffMs      = deadline - today;
  const remainingDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  return {
    deadline,
    remainingDays,
    isOverdue:  remainingDays < 0,
    isDueToday: remainingDays === 0,
    isDueSoon:  remainingDays > 0 && remainingDays <= 7,
  };
};

function DeadlineBadge({ requestedAt, withdrawalPeriod, transferStatus }) {
  // If already completed/failed, don't show urgency colours
  const isDone = transferStatus === 'completed' || transferStatus === 'failed';
  const info   = getDeadlineInfo(requestedAt, withdrawalPeriod);
  if (!info) return null;

  let bg, color, label;

  if (isDone) {
    bg    = 'rgba(255,255,255,0.07)';
    color = 'rgba(255,255,255,0.4)';
    label = `Due ${info.deadline.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`;
  } else if (info.isOverdue) {
    bg    = 'rgba(239,68,68,0.2)';
    color = '#fca5a5';
    label = `${Math.abs(info.remainingDays)} day${Math.abs(info.remainingDays) !== 1 ? 's' : ''} overdue`;
  } else if (info.isDueToday) {
    bg    = 'rgba(245,158,11,0.25)';
    color = '#fcd34d';
    label = 'Due today';
  } else if (info.isDueSoon) {
    bg    = 'rgba(245,158,11,0.15)';
    color = '#fbbf24';
    label = `${info.remainingDays} day${info.remainingDays !== 1 ? 's' : ''} left`;
  } else {
    bg    = 'rgba(5,150,105,0.15)';
    color = '#6ee7b7';
    label = `${info.remainingDays} day${info.remainingDays !== 1 ? 's' : ''} left`;
  }

  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold"
          style={{ background: bg, color }}>
      <Clock size={9} />
      {label}
    </span>
  );
}

const transferStatusConfig = {
  pending:     { color: '#fcd34d', bg: 'rgba(245,158,11,0.2)',  label: 'Pending',     icon: Clock },
  in_progress: { color: '#93c5fd', bg: 'rgba(59,130,246,0.2)', label: 'In Progress', icon: Loader2 },
  completed:   { color: '#6ee7b7', bg: 'rgba(5,150,105,0.2)',  label: 'Completed',   icon: CheckCircle2 },
  failed:      { color: '#fca5a5', bg: 'rgba(239,68,68,0.2)',  label: 'Failed',      icon: AlertCircle },
};

function TransferStatusBadge({ status }) {
  const cfg = transferStatusConfig[status] || transferStatusConfig.pending;
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold"
          style={{ background: cfg.bg, color: cfg.color }}>
      {cfg.label}
    </span>
  );
}

// ── Skeleton ─────────────────────────────────────────────────
function TransferSkeleton() {
  return (
    <div className="card flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <div className="skeleton h-10 w-10 flex-shrink-0 rounded-full" />
        <div className="flex-1 flex flex-col gap-2">
          <div className="skeleton h-3.5 w-28 rounded" />
          <div className="skeleton h-3 w-20 rounded" />
        </div>
        <div className="skeleton h-5 w-16 rounded" />
      </div>
      <div className="skeleton h-12 rounded-xl" />
      <div className="skeleton h-10 rounded-xl" />
    </div>
  );
}

// ── Update Transfer Sheet ─────────────────────────────────────
function UpdateTransferSheet({ transfer, onClose, onSuccess }) {
  const [method,     setMethod]     = useState(transfer.transfer_method || '');
  const [status,     setStatus]     = useState(transfer.transfer_status || 'pending');
  const [notes,      setNotes]      = useState(transfer.transfer_notes || '');
  const [submitting, setSubmitting] = useState(false);
  const [apiError,   setApiError]   = useState('');

  const handleSubmit = async () => {
    setSubmitting(true);
    setApiError('');
    try {
      const payload = {
        transfer_status: status,
        ...(method ? { transfer_method: method } : {}),
        transfer_notes: notes.trim() || null,
      };
      const { data } = await withdrawalsAPI.updateTransfer(transfer.id, payload);
      if (data.status) {
        onSuccess(transfer.id, data.data);
        onClose();
      } else {
        setApiError(data.message || 'Update failed.');
      }
    } catch (err) {
      setApiError(err?.response?.data?.message || 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
      onClick={!submitting ? onClose : undefined}
    >
      <div
        className="w-full max-w-app animate-fade-up rounded-t-3xl p-6 pb-8"
        style={{
          background: 'linear-gradient(180deg,#0e2060 0%,#060f38 100%)',
          border: '1px solid rgba(212,160,23,0.2)',
          borderBottom: 'none',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h3 className="font-serif text-base font-semibold text-white">
              Update Transfer
            </h3>
            <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.45)' }}>
              {transfer.user?.name ?? 'Deleted User'} · {formatINR(transfer.amount)}
            </p>
          </div>
          {!submitting && (
            <button onClick={onClose}
                    className="flex h-7 w-7 items-center justify-center rounded-xl"
                    style={{ background: 'rgba(255,255,255,0.08)' }}>
              <X size={14} className="text-white" />
            </button>
          )}
        </div>

        {/* Deadline pill */}
        {(() => {
          const info = getDeadlineInfo(transfer.requested_at, transfer.withdrawal_period);
          return (
            <div className="mb-4 flex items-center gap-2 rounded-xl px-3 py-2.5"
                 style={{ background: 'rgba(212,160,23,0.08)', border: '1px solid rgba(212,160,23,0.2)' }}>
              <Clock size={12} style={{ color: '#d4a017', flexShrink: 0 }} />
              <div className="flex-1">
                <span className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>
                  Transfer deadline:&nbsp;
                </span>
                <span className="text-xs font-semibold" style={{ color: '#f0c040' }}>
                  {info
                    ? info.deadline.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                    : formatPeriod(transfer.withdrawal_period)}
                </span>
              </div>
              <DeadlineBadge
                requestedAt={transfer.requested_at}
                withdrawalPeriod={transfer.withdrawal_period}
                transferStatus={transfer.transfer_status}
              />
            </div>
          );
        })()}

        {/* API error */}
        {apiError && (
          <div className="mb-3 flex items-center gap-2 rounded-xl px-3 py-2.5 text-xs"
               style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5' }}>
            <AlertCircle size={13} className="flex-shrink-0" />
            <span>{apiError}</span>
          </div>
        )}

        <div className="flex flex-col gap-4">

          {/* Transfer Method */}
          <div className="flex flex-col gap-1.5">
            <label className="input-label">Transfer Method</label>
            <div className="grid grid-cols-3 gap-2">
              {TRANSFER_METHODS.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  disabled={submitting}
                  onClick={() => setMethod(m.value)}
                  className="rounded-xl py-2 text-xs font-semibold transition-all duration-150 disabled:opacity-50"
                  style={{
                    background: method === m.value
                      ? 'linear-gradient(135deg,#d4a017,#f0c040)'
                      : 'rgba(255,255,255,0.07)',
                    color: method === m.value ? '#fff' : 'rgba(255,255,255,0.55)',
                    border: method === m.value ? 'none' : '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* Transfer Status */}
          <div className="flex flex-col gap-1.5">
            <label className="input-label">Transfer Status</label>
            <div className="grid grid-cols-2 gap-2">
              {TRANSFER_STATUSES.filter(s => s.value !== 'all').map((s) => {
                const cfg = transferStatusConfig[s.value];
                return (
                  <button
                    key={s.value}
                    type="button"
                    disabled={submitting}
                    onClick={() => setStatus(s.value)}
                    className="rounded-xl py-2.5 text-xs font-semibold transition-all duration-150 disabled:opacity-50"
                    style={{
                      background: status === s.value ? cfg.bg : 'rgba(255,255,255,0.05)',
                      color:      status === s.value ? cfg.color : 'rgba(255,255,255,0.45)',
                      border: `1px solid ${status === s.value ? cfg.color + '55' : 'rgba(255,255,255,0.08)'}`,
                    }}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div className="flex flex-col gap-1.5">
            <label className="input-label">
              Notes <span style={{ color: 'rgba(255,255,255,0.3)' }}>(optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Transferred via NEFT, ref #12345"
              rows={2}
              maxLength={1000}
              disabled={submitting}
              className="input-field resize-none disabled:opacity-50"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={submitting}
              className="flex-1 rounded-2xl py-3 text-sm font-semibold
                         transition-all active:scale-95 disabled:opacity-50"
              style={{
                background: 'rgba(255,255,255,0.07)',
                border: '1px solid rgba(255,255,255,0.12)',
                color: 'rgba(255,255,255,0.7)',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || !status}
              className="flex-1 inline-flex items-center justify-center gap-2
                         rounded-2xl py-3 text-sm font-semibold transition-all
                         active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{
                background: 'linear-gradient(135deg,#d4a017 0%,#f0c040 100%)',
                color: '#fff',
              }}
            >
              {submitting
                ? <span className="inline-block h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                : 'Save Changes'
              }
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

// ── Transfer Card ─────────────────────────────────────────────
function TransferCard({ transfer, onAction }) {
  const userName = transfer.user?.name ?? 'Deleted User';
  const initials = userName
    .split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase();

  const methodLabel = TRANSFER_METHODS.find((m) => m.value === transfer.transfer_method)?.label;

  return (
    <div className="card flex flex-col gap-3 transition-all duration-200">

      {/* Top row */}
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center
                        rounded-full text-xs font-bold text-white"
             style={{ background: 'linear-gradient(135deg,#1448c4 0%,#0a2260 100%)' }}>
          {initials}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white truncate">{userName}</p>
          <p className="text-xs mt-0.5 truncate" style={{ color: 'rgba(255,255,255,0.45)' }}>
            {transfer.user?.mobile ?? '—'}
          </p>
        </div>

        <div className="flex-shrink-0 text-right">
          <p className="text-sm font-bold text-gold-shimmer">
            {formatINR(transfer.amount)}
          </p>
          <span className="inline-block mt-0.5 rounded-full px-2 py-0.5 text-[10px] font-medium"
                style={{ background: 'rgba(212,160,23,0.12)', color: '#d4a017' }}>
            {formatPeriod(transfer.withdrawal_period)}
          </span>
        </div>
      </div>

      {/* Deadline row */}
      <div className="flex items-center gap-2 rounded-xl px-3 py-2"
           style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <Clock size={11} style={{ color: 'rgba(255,255,255,0.3)', flexShrink: 0 }} />
        <span className="text-[11px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
          Deadline:&nbsp;
          <span style={{ color: 'rgba(255,255,255,0.65)' }}>
            {(() => {
              const info = getDeadlineInfo(transfer.requested_at, transfer.withdrawal_period);
              return info
                ? info.deadline.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                : '—';
            })()}
          </span>
        </span>
        <div className="ml-auto">
          <DeadlineBadge
            requestedAt={transfer.requested_at}
            withdrawalPeriod={transfer.withdrawal_period}
            transferStatus={transfer.transfer_status}
          />
        </div>
      </div>

      {/* Bank account */}
      {transfer.user?.bank_account && (
        <div className="flex items-center gap-2 rounded-xl px-3 py-2"
             style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <Landmark size={12} style={{ color: '#d4a017', flexShrink: 0 }} />
          <div className="flex-1 min-w-0">
            <p className="text-[11px] truncate" style={{ color: 'rgba(255,255,255,0.5)' }}>
              {transfer.user.bank_account.bank_name} ·{' '}
              <span style={{ color: 'rgba(255,255,255,0.7)' }}>
                {transfer.user.bank_account.account_number}
              </span>
              {transfer.user.bank_account.ifsc && (
                <span style={{ color: 'rgba(255,255,255,0.4)' }}>
                  {' '}· {transfer.user.bank_account.ifsc}
                </span>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Transfer method + status row */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {methodLabel ? (
            <span className="inline-block rounded-full px-2.5 py-0.5 text-[10px] font-semibold"
                  style={{ background: 'rgba(212,160,23,0.12)', color: '#f0c040' }}>
              {methodLabel}
            </span>
          ) : (
            <span className="text-[11px] italic" style={{ color: 'rgba(255,255,255,0.3)' }}>
              Method not set
            </span>
          )}
          <TransferStatusBadge status={transfer.transfer_status} />
        </div>
        <button
          onClick={() => onAction(transfer)}
          className="rounded-xl px-3 py-1 text-xs font-semibold transition-all active:scale-95"
          style={{
            background: 'rgba(212,160,23,0.15)',
            border: '1px solid rgba(212,160,23,0.3)',
            color: '#d4a017',
          }}
        >
          Update
        </button>
      </div>

      {/* Notes + dates */}
      <div className="flex flex-col gap-1">
        {transfer.transfer_notes && (
          <p className="text-[11px] italic px-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
            {transfer.transfer_notes}
          </p>
        )}
        <div className="flex items-center justify-between">
          <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.3)' }}>
            Approved: {formatDate(transfer.approved_at)}
          </p>
          {transfer.transferred_at && (
            <p className="text-[10px]" style={{ color: '#6ee7b7' }}>
              Transferred: {formatDate(transfer.transferred_at)}
            </p>
          )}
        </div>
      </div>

    </div>
  );
}

// ── Main page ────────────────────────────────────────────────
export default function WithdrawalTransfersPage() {
  // filters
  const [search,          setSearch]          = useState('');
  const [transferStatus,  setTransferStatus]  = useState('all');
  const [from,            setFrom]            = useState('');
  const [to,              setTo]              = useState('');
  const [showFilters,     setShowFilters]     = useState(false);

  // data
  const [transfers,   setTransfers]   = useState([]);
  const [pagination,  setPagination]  = useState(null);
  const [page,        setPage]        = useState(1);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');

  // action sheet
  const [activeTransfer, setActiveTransfer] = useState(null);
  const [successMsg,     setSuccessMsg]     = useState('');

  // debounce
  const searchTimer = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 450);
    return () => clearTimeout(searchTimer.current);
  }, [search]);

  useEffect(() => { setPage(1); }, [transferStatus, from, to]);

  // ── Fetch ──────────────────────────────────────────────────
  const fetchTransfers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, per_page: PER_PAGE };
      if (debouncedSearch)           params.search          = debouncedSearch;
      if (transferStatus !== 'all')  params.transfer_status = transferStatus;
      if (from)                      params.from            = from;
      if (to)                        params.to              = to;

      const { data } = await withdrawalsAPI.getTransfers(params);
      if (data.status) {
        setTransfers(data.data.transfers);
        setPagination(data.data.pagination);
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load transfer records.');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, transferStatus, from, to, page]);

  useEffect(() => { fetchTransfers(); }, [fetchTransfers]);

  // Auto-dismiss success banner
  useEffect(() => {
    if (!successMsg) return;
    const t = setTimeout(() => setSuccessMsg(''), 4000);
    return () => clearTimeout(t);
  }, [successMsg]);

  // Update card in-place
  const handleUpdateSuccess = (id, updatedData) => {
    setTransfers((prev) =>
      prev.map((t) =>
        t.id === id ? {
          ...t,
          transfer_method: updatedData.transfer_method,
          transfer_status: updatedData.transfer_status,
          transfer_notes:  updatedData.transfer_notes,
          transferred_at:  updatedData.transferred_at,
        } : t
      )
    );
    setSuccessMsg('Transfer updated successfully');
  };

  const clearFilters = () => { setTransferStatus('all'); setFrom(''); setTo(''); setPage(1); };
  const hasActiveFilters = transferStatus !== 'all' || from || to;

  // ── Render ───────────────────────────────────────────────
  return (
    <div className="flex flex-col min-h-0">
      <PageHeader
        title="Transfer Tracking"
        showBack
        right={
          <button
            onClick={() => setShowFilters((v) => !v)}
            className="relative flex h-8 w-8 items-center justify-center rounded-xl
                       transition-all active:scale-90"
            style={{
              background: showFilters ? 'rgba(212,160,23,0.2)' : 'rgba(255,255,255,0.08)',
              border: showFilters
                ? '1px solid rgba(212,160,23,0.4)'
                : '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <SlidersHorizontal size={15}
              style={{ color: showFilters ? '#d4a017' : 'rgba(255,255,255,0.6)' }} />
            {hasActiveFilters && (
              <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full"
                    style={{ background: '#d4a017' }} />
            )}
          </button>
        }
      />

      <div className="flex flex-col gap-3 p-4">

        {/* ── Success banner ── */}
        {successMsg && (
          <div className="flex items-center gap-2 rounded-xl px-4 py-3 text-sm animate-fade-in"
               style={{ background: 'rgba(5,150,105,0.15)', border: '1px solid rgba(5,150,105,0.35)', color: '#6ee7b7' }}>
            <CheckCircle2 size={15} className="flex-shrink-0" />
            <span>{successMsg}</span>
          </div>
        )}

        {/* ── Search ── */}
        <div className="relative">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2"
                  style={{ color: 'rgba(255,255,255,0.35)' }} />
          <input
            type="text"
            placeholder="Search by user name or mobile..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-field pl-9 pr-9"
          />
          {search && (
            <button onClick={() => setSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    style={{ color: 'rgba(255,255,255,0.4)' }}>
              <X size={14} />
            </button>
          )}
        </div>

        {/* ── Filter panel ── */}
        {showFilters && (
          <div className="card flex flex-col gap-4 animate-fade-in">
            <div>
              <p className="input-label mb-2">Transfer Status</p>
              <div className="flex flex-wrap gap-2">
                {TRANSFER_STATUSES.map((s) => {
                  const isActive  = transferStatus === s.value;
                  const cfg       = s.value !== 'all' ? transferStatusConfig[s.value] : null;
                  return (
                    <button
                      key={s.value}
                      onClick={() => setTransferStatus(s.value)}
                      className="rounded-xl px-3 py-1.5 text-xs font-semibold capitalize
                                 transition-all duration-150"
                      style={{
                        background: isActive
                          ? cfg ? cfg.bg : 'linear-gradient(135deg,#d4a017,#f0c040)'
                          : 'rgba(255,255,255,0.07)',
                        color: isActive
                          ? cfg ? cfg.color : '#fff'
                          : 'rgba(255,255,255,0.5)',
                        border: isActive ? 'none' : '1px solid rgba(255,255,255,0.1)',
                      }}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col">
                <label className="input-label">From</label>
                <input type="date" value={from}
                       onChange={(e) => setFrom(e.target.value)}
                       className="input-field" />
              </div>
              <div className="flex flex-col">
                <label className="input-label">To</label>
                <input type="date" value={to}
                       onChange={(e) => setTo(e.target.value)}
                       className="input-field" />
              </div>
            </div>

            {hasActiveFilters && (
              <button onClick={clearFilters}
                      className="flex items-center gap-1.5 self-start text-xs font-medium"
                      style={{ color: '#fca5a5' }}>
                <X size={12} /> Clear filters
              </button>
            )}
          </div>
        )}

        {/* ── Summary ── */}
        {pagination && !loading && (
          <p className="text-xs px-1" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Showing {transfers.length} of {pagination.total} transfer{pagination.total !== 1 ? 's' : ''}
          </p>
        )}

        {/* ── Error ── */}
        {error && (
          <div className="card py-6 text-sm text-center" style={{ color: '#fca5a5' }}>
            {error}
          </div>
        )}

        {/* ── Loading ── */}
        {loading && !error && (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 5 }).map((_, i) => <TransferSkeleton key={i} />)}
          </div>
        )}

        {/* ── Transfer list ── */}
        {!loading && !error && transfers.length > 0 && (
          <div className="flex flex-col gap-3">
            {transfers.map((t) => (
              <TransferCard
                key={t.id}
                transfer={t}
                onAction={setActiveTransfer}
              />
            ))}
          </div>
        )}

        {/* ── Empty state ── */}
        {!loading && !error && transfers.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-full"
                 style={{ background: 'rgba(255,255,255,0.06)' }}>
              <ArrowRightLeft size={24} style={{ color: 'rgba(255,255,255,0.3)' }} />
            </div>
            <p className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.4)' }}>
              {debouncedSearch || hasActiveFilters
                ? 'No transfers match your filters'
                : 'No approved withdrawals pending transfer'}
            </p>
            {(debouncedSearch || hasActiveFilters) && (
              <button
                onClick={() => { setSearch(''); clearFilters(); }}
                className="text-xs font-medium"
                style={{ color: '#d4a017' }}
              >
                Clear all filters
              </button>
            )}
          </div>
        )}

        {/* ── Pagination ── */}
        {!loading && pagination && pagination.last_page > 1 && (
          <div className="flex items-center justify-between pt-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium
                         transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.7)' }}
            >
              <ChevronLeft size={15} /> Prev
            </button>
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>
              Page {pagination.current_page} / {pagination.last_page}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(pagination.last_page, p + 1))}
              disabled={page === pagination.last_page}
              className="flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium
                         transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.7)' }}
            >
              Next <ChevronRight size={15} />
            </button>
          </div>
        )}

      </div>

      {/* ── Update Transfer Sheet ── */}
      {activeTransfer && (
        <UpdateTransferSheet
          transfer={activeTransfer}
          onClose={() => setActiveTransfer(null)}
          onSuccess={handleUpdateSuccess}
        />
      )}
    </div>
  );
}
